import { PlusCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { Suspense, useState } from 'react';

import { Entitled } from '@/components/Entitled';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { InviteUser } from '@/components/legacy-ui/InviteUser';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { SettingSection } from '@/sections/settings/components';
import { UserManagementCards } from '@/sections/settings/components/cards/UserManagementCards';
import { useAuth } from '@/state/auth';

/**
 * UserManagementTab wraps UserManagementCards with Suspense and ErrorBoundary.
 *
 * This is rendered as the "User Management" tab in the Settings page.
 * User Profile settings are in a separate page (/profile) accessible from AccountDropdown.
 *
 * IMPORTANT: This component RESPECTS impersonation.
 * When impersonating, shows user tables for the impersonated account.
 *
 * Features:
 * - Authorized Users table with "Add User" action
 * - Collaborating With table (only when viewing own account)
 * - Read-only mode when impersonating
 *
 * Impersonation Behavior:
 * - Not impersonating: Shows own account's users, can add/remove users
 * - Impersonating: Shows impersonated account's users, read-only mode
 *
 * React 19 Features:
 * - Suspense: Progressive loading for async user data
 * - ErrorBoundary: Graceful error handling for API failures
 *
 * @example
 * ```tsx
 * // Used in Settings page as "User Management" tab
 * {selectedTab === 'userManagement' && <UserManagementTab />}
 * ```
 *
 * @see {@link UserManagementCards} Core user management implementation
 * @see {@link Profile} Separate page for user profile settings (ignores impersonation)
 */
export function UserManagementTab() {
  const { isImpersonating, friend } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);

  return (
    <>
      <SettingSection
        title="User Management"
        description={
          isImpersonating
            ? 'Manage users with access to this account.'
            : 'Manage users with access to your account. Add or remove authorized users and view collaborators.'
        }
        action={
          <Entitled entitlement="manage_accounts">
            <Button
              onClick={() => setShowInviteModal(true)}
              size="sm"
              data-testid="add-user-button"
            >
              <PlusCircleIcon className="size-4" />
              Add User
            </Button>
          </Entitled>
        }
        headerOnly
      >
        <ErrorBoundary
          fallback={
            <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
              <XCircleIcon className="text-chariot-status-critical size-12" />
              <div className="flex flex-col gap-2 text-center">
                <h2 className="text-chariot-text-primary text-lg font-bold">
                  Failed to Load User Management
                </h2>
                <p className="text-chariot-text-secondary max-w-md text-sm">
                  Unable to retrieve user management data. Please refresh the
                  page or contact support.
                </p>
              </div>
            </div>
          }
        >
          <Suspense
            fallback={
              <div className="flex h-full flex-col items-center justify-center gap-4 py-12">
                <Spinner size="xl" />
                <p className="text-chariot-text-secondary text-sm">
                  Loading user management...
                </p>
              </div>
            }
          >
            <UserManagementCards email={isImpersonating ? friend : undefined} />
          </Suspense>
        </ErrorBoundary>
      </SettingSection>

      {/* Invite User Modal */}
      <InviteUser
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        customerEmail={isImpersonating ? friend : undefined}
      />
    </>
  );
}
